import { createId, todayIso, type CrashLog } from '@shared/domain';
import { getBuildNumber, getDeviceInfo, getAppVersion } from './appInfo';
import { listCrashLogsLocal, saveCrashLogLocal } from './localData';

let installed = false;
let lastSignature = '';
let lastLoggedAt = 0;

function currentRoute(): string {
  if (typeof window === 'undefined') return '/';
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function normalizeError(error: unknown): { message: string; stack?: string } {
  if (error instanceof Error) {
    return {
      message: error.message || error.name || 'Unknown error',
      ...(error.stack ? { stack: error.stack } : {})
    };
  }

  if (typeof error === 'string') {
    return { message: error };
  }

  try {
    return { message: JSON.stringify(error) };
  } catch {
    return { message: 'Unknown error' };
  }
}

function shouldSkip(signature: string): boolean {
  const now = Date.now();
  if (signature === lastSignature && now - lastLoggedAt < 2500) {
    return true;
  }
  lastSignature = signature;
  lastLoggedAt = now;
  return false;
}

export function recordCrashLog(input: {
  type: CrashLog['type'];
  error: unknown;
  componentStack?: string;
}): CrashLog | null {
  try {
    const normalized = normalizeError(input.error);
    const route = currentRoute();
    const signature = `${input.type}:${route}:${normalized.message}`;
    if (shouldSkip(signature)) {
      return null;
    }

    return saveCrashLogLocal({
      id: createId('crash'),
      timestamp: todayIso(),
      type: input.type,
      message: normalized.message,
      ...(normalized.stack ? { stack: normalized.stack } : {}),
      ...(input.componentStack ? { componentStack: input.componentStack } : {}),
      route,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      appVersion: getAppVersion(),
      buildNumber: getBuildNumber(),
      deviceInfo: getDeviceInfo(route)
    });
  } catch {
    return null;
  }
}

export function installCrashDiagnostics(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event) => {
    recordCrashLog({
      type: 'window',
      error: event.error ?? event.message
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    recordCrashLog({
      type: 'promise',
      error: event.reason
    });
  });
}

export function getCrashLogsSnapshot(): CrashLog[] {
  return listCrashLogsLocal();
}

export function buildCrashLogsExport(logs = getCrashLogsSnapshot()): string {
  return JSON.stringify(
    {
      exportedAt: todayIso(),
      appVersion: getAppVersion(),
      buildNumber: getBuildNumber(),
      count: logs.length,
      logs
    },
    null,
    2
  );
}
